import React, { useEffect, useMemo, useState } from 'react';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { usePitches } from '../context/PitchesContext';
import { useBrandPitches } from '../context/BrandPitchesContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { FileEdit, Plus, Trash2, Send, Check, ChevronsUpDown, Copy, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import InitialsAvatar from '../components/InitialsAvatar';

// -----------------------------------------------------------------------------
const PitchTemplates = () => {
  const { talent, brands, brandMap, talentMap } = useDeals();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const { refresh: refreshOutbound } = usePitches();
  const { refresh: refreshBrandPitches } = useBrandPitches();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedByTalent, setSelectedByTalent] = useState({});
  const [filters, setFilters] = useState({ talent: 'all', brand: 'all', q: '' });
  const setSelected = (key, id) => setSelectedByTalent((prev) => ({ ...prev, [key]: prev[key] === id ? null : id }));

  const reload = async () => {
    try {
      const { data } = await api.get('/pitching/templates');
      setTemplates(data || []);
    } catch { toast.error('failed to load templates'); }
  };

  useEffect(() => {
    (async () => { setLoading(true); await reload(); setLoading(false); })();
  }, []);

  const filteredTemplates = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return templates.filter((t) => {
      if (filters.talent !== 'all' && t.talent_id !== filters.talent) return false;
      if (filters.brand !== 'all' && !(t.brand_ids || []).includes(filters.brand)) return false;
      if (q && !(t.campaign_name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [templates, filters]);

  const globalDrafts = useMemo(() => filteredTemplates.filter((t) => !t.talent_id), [filteredTemplates]);
  const perTalent = useMemo(() => {
    const map = new Map();
    (isManager ? talent : talent.filter((t) => t.user_id === user?.user_id))
      .filter((t) => filters.talent === 'all' || t.user_id === filters.talent)
      .forEach((t) => map.set(t.user_id, []));
    filteredTemplates.filter((t) => t.talent_id).forEach((t) => {
      if (!map.has(t.talent_id)) map.set(t.talent_id, []);
      map.get(t.talent_id).push(t);
    });
    return Array.from(map.entries()).map(([id, items]) => ({ talent: talent.find((t) => t.user_id === id), items }))
      .sort((a, b) => (a.talent?.name || '').localeCompare(b.talent?.name || ''));
  }, [filteredTemplates, talent, isManager, user, filters.talent]);

  const create = async (talent_id) => {
    try {
      const { data } = await api.post('/pitching/templates', { talent_id: talent_id || null, campaign_name: 'new campaign', copy: '' });
      setTemplates((prev) => [data, ...prev]);
      setSelected(talent_id || '__global__', data.id);
    } catch { toast.error('add failed'); }
  };

  const patch = async (id, updates) => {
    try {
      const { data } = await api.patch(`/pitching/templates/${id}`, updates);
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, ...data } : t)));
    } catch { toast.error('save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('delete this template?')) return;
    try {
      await api.delete(`/pitching/templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch { toast.error('delete failed'); }
  };

  const duplicate = async (id, target_talent_id) => {
    try {
      const { data } = await api.post(`/pitching/templates/${id}/duplicate`, null, { params: target_talent_id ? { target_talent_id } : {} });
      setTemplates((prev) => [data, ...prev]);
      setSelected(target_talent_id || '__global__', data.id);
      toast.success('copied');
    } catch { toast.error('duplicate failed'); }
  };

  const sendBrand = async (tpl, brand_id) => {
    if (!tpl.talent_id) { toast.error('assign this to a talent first'); return; }
    try {
      const { data } = await api.post(`/pitching/templates/${tpl.id}/send-brand`, null, { params: { brand_id } });
      setTemplates((prev) => prev.map((t) => (t.id === data.id ? { ...t, ...data } : t)));
      refreshOutbound?.();
      refreshBrandPitches?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'send failed'); }
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <FileEdit className="w-6 h-6 text-pink-500" /> templates
        </h1>
        <p className="text-sm text-slate-500 mt-1">draft campaign copy per talent, pick the brands you're sending it to, then tick send per brand as you send them.</p>
      </header>

      <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-5 flex flex-wrap items-center gap-2">
        <Input
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          placeholder="search campaign name…"
          className="h-9 w-56 text-xs"
          data-testid="tpl-filter-name"
        />
        <FilterPicker
          label="talent"
          value={filters.talent}
          onChange={(v) => setFilters({ ...filters, talent: v })}
          items={[{ value: 'all', label: 'all talent' }, ...talent.map((t) => ({ value: t.user_id, label: t.name }))]}
          testId="tpl-filter-talent"
        />
        <FilterPicker
          label="brand"
          value={filters.brand}
          onChange={(v) => setFilters({ ...filters, brand: v })}
          items={[{ value: 'all', label: 'all brands' }, ...brands.map((b) => ({ value: b.id, label: `${b.logo ? b.logo + ' ' : ''}${b.name}` }))]}
          testId="tpl-filter-brand"
        />
        <div className="ml-auto text-[11px] text-slate-500">{filteredTemplates.length} template{filteredTemplates.length === 1 ? '' : 's'}</div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">loading…</div>
      ) : (
        <div className="space-y-6">
          {/* Global drafts shelf — starter copy that can be duplicated onto a talent */}
          {isManager && (
            <section className="bg-white border-2 border-dashed border-pink-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-pink-500" />
                <h2 className="text-sm font-semibold text-slate-800">starter drafts</h2>
                <span className="text-[11px] text-slate-500">— write once, duplicate onto any talent</span>
              </div>
              <TemplateBubbles
                items={globalDrafts}
                talent={null}
                selectedId={selectedByTalent['__global__']}
                onSelect={(id) => setSelected('__global__', id)}
                onCreate={() => create(null)}
                canEdit={isManager}
              />
              {selectedByTalent['__global__'] && (
                <div className="mt-3">
                  {(() => {
                    const tpl = globalDrafts.find((t) => t.id === selectedByTalent['__global__']);
                    if (!tpl) return null;
                    return (
                      <TemplateEditor
                        template={tpl}
                        brands={brands}
                        brandMap={brandMap}
                        talent={null}
                        allTalent={talent}
                        canEdit={isManager}
                        isManager={isManager}
                        onPatch={(u) => patch(tpl.id, u)}
                        onRemove={() => remove(tpl.id)}
                        onSendBrand={(brand_id) => sendBrand(tpl, brand_id)}
                        onDuplicate={(target) => duplicate(tpl.id, target)}
                      />
                    );
                  })()}
                </div>
              )}
            </section>
          )}

          {/* Per-talent blocks */}
          {perTalent.map((g) => {
            const selId = selectedByTalent[g.talent?.user_id];
            const selectedTpl = selId ? g.items.find((t) => t.id === selId) : null;
            return (
              <TalentTemplateBlock
                key={g.talent?.user_id || '__none__'}
                talent={g.talent}
                items={g.items}
                brands={brands}
                brandMap={brandMap}
                allTalent={talent}
                canEdit={isManager || g.talent?.user_id === user?.user_id}
                isManager={isManager}
                selectedTpl={selectedTpl}
                onSelect={(id) => setSelected(g.talent.user_id, id)}
                onCreate={() => create(g.talent.user_id)}
                onPatch={patch}
                onRemove={remove}
                onDuplicate={duplicate}
                onSendBrand={sendBrand}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
const TemplateBubbles = ({ items, talent, selectedId, onSelect, onCreate, canEdit }) => {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {items.length === 0 && (
        <span className="text-[12px] italic text-slate-400 mr-2">no drafts yet</span>
      )}
      {items.map((tpl) => {
        const active = tpl.id === selectedId;
        const total = (tpl.brand_ids || []).length;
        const sent = (tpl.sent_brand_ids || []).length;
        return (
          <button
            key={tpl.id}
            type="button"
            onClick={() => onSelect(tpl.id)}
            className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-semibold border transition ${
              active
                ? 'bg-pink-500 text-white border-pink-500 shadow-sm'
                : 'bg-pink-50 text-pink-700 border-pink-200 hover:border-pink-400'
            }`}
            data-testid={`tpl-bubble-${tpl.id}`}
          >
            <span className="truncate max-w-[180px]">{tpl.campaign_name || 'untitled'}</span>
            <span className={`tabular-nums text-[10px] ${active ? 'opacity-80' : 'opacity-60'}`}>
              {sent > 0 ? `${sent}/${total}` : total}
            </span>
          </button>
        );
      })}
      {canEdit && (
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold border border-dashed border-pink-300 text-pink-600 hover:bg-pink-50"
          data-testid={talent ? `tpl-new-${talent.user_id}` : 'tpl-new-global'}
        >
          <Plus className="w-3 h-3" /> new
        </button>
      )}
    </div>
  );
};

const FilterPicker = ({ label, value, onChange, items, testId }) => {
  const [open, setOpen] = useState(false);
  const current = items.find((i) => i.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 bg-white text-xs hover:border-pink-300 min-w-[140px]"
          data-testid={testId}
        >
          <span className="text-slate-400 uppercase text-[9px] tracking-widest">{label}</span>
          <span className="text-slate-700 font-medium truncate">{current?.label || 'all'}</span>
          <ChevronsUpDown className="w-3 h-3 opacity-40 ml-auto" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`search ${label}…`} className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>none</CommandEmpty>
            <CommandGroup>
              {items.map((i) => (
                <CommandItem key={i.value} value={i.label} onSelect={() => { onChange(i.value); setOpen(false); }}>
                  <Check className={`mr-2 h-3.5 w-3.5 ${i.value === value ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{i.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// -----------------------------------------------------------------------------
const TalentTemplateBlock = ({ talent, items, brands, brandMap, allTalent, canEdit, isManager, selectedTpl, onSelect, onCreate, onPatch, onRemove, onDuplicate, onSendBrand }) => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-pink-200" style={{ background: '#d394ab' }}>
        {talent ? <InitialsAvatar name={talent.name} size={32} /> : <div className="w-8 h-8 rounded-full bg-white/30 border border-dashed border-white/50" />}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-white truncate">{talent?.name || 'unassigned'}</div>
          <div className="text-xs text-white/80">{items.length} template{items.length === 1 ? '' : 's'}</div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <TemplateBubbles
          items={items}
          talent={talent}
          selectedId={selectedTpl?.id}
          onSelect={onSelect}
          onCreate={onCreate}
          canEdit={canEdit}
        />
        {selectedTpl && (
          <TemplateEditor
            template={selectedTpl}
            brands={brands}
            brandMap={brandMap}
            talent={talent}
            allTalent={allTalent}
            canEdit={canEdit}
            isManager={isManager}
            onPatch={(u) => onPatch(selectedTpl.id, u)}
            onRemove={() => onRemove(selectedTpl.id)}
            onSendBrand={(brand_id) => onSendBrand(selectedTpl, brand_id)}
            onDuplicate={(target) => onDuplicate(selectedTpl.id, target)}
          />
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
const TemplateEditor = ({ template, brands, brandMap, talent, allTalent, canEdit, isManager, onPatch, onRemove, onSendBrand, onDuplicate }) => {
  const [name, setName] = useState(template.campaign_name || '');
  const [copy, setCopy] = useState(template.copy || '');
  useEffect(() => { setName(template.campaign_name || ''); }, [template.campaign_name, template.id]);
  useEffect(() => { setCopy(template.copy || ''); }, [template.copy, template.id]);

  const isGlobal = !template.talent_id;
  const brandIds = template.brand_ids || [];
  const sentBrandIds = template.sent_brand_ids || [];

  const removeBrand = (id) => {
    if (!canEdit) return;
    onPatch({ brand_ids: brandIds.filter((x) => x !== id) });
  };
  const addBrand = (id) => {
    if (!canEdit || brandIds.includes(id)) return;
    onPatch({ brand_ids: [...brandIds, id] });
  };

  return (
    <div className="rounded-xl border-2 border-pink-100 bg-pink-50/20 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold">campaign</label>
          <div className="bg-pink-50 border-2 border-pink-200 rounded-2xl px-3 py-1.5 mt-0.5">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => { if (name !== (template.campaign_name || '')) onPatch({ campaign_name: name }); }}
              disabled={!canEdit}
              placeholder="name this campaign"
              className="h-6 px-0 text-sm font-semibold text-pink-800 bg-transparent border-none outline-none focus-visible:ring-0 placeholder:text-pink-300"
              data-testid={`tpl-name-${template.id}`}
            />
          </div>
        </div>
        {isManager && (
          <DuplicatePicker
            talents={allTalent}
            excludeId={template.talent_id}
            onPick={(id) => onDuplicate(id)}
          />
        )}
        {canEdit && (
          <button onClick={onRemove} className="text-slate-300 hover:text-red-600 shrink-0" title="delete template">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="px-4 pb-3 space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold">copy</label>
          <Textarea
            value={copy}
            onChange={(e) => setCopy(e.target.value)}
            onBlur={() => { if (copy !== (template.copy || '')) onPatch({ copy }); }}
            disabled={!canEdit}
            rows={6}
            placeholder="hi [brand]…"
            className="mt-1 text-sm border-pink-200 focus-visible:ring-pink-300 bg-white"
            data-testid={`tpl-copy-${template.id}`}
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-pink-500 font-semibold">brands</label>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {brandIds.map((bid) => {
              const b = brandMap[bid];
              const sent = sentBrandIds.includes(bid);
              return (
                <span
                  key={bid}
                  className="inline-flex items-center h-8 pl-3 pr-1 rounded-full text-[12px] font-semibold group"
                  style={{
                    background: sent ? '#dcfce7' : '#f3ecfb',
                    color: sent ? '#166534' : '#5b21b6',
                    border: `1px solid ${sent ? '#86efac' : '#d3c1e1'}`,
                  }}
                  data-testid={`tpl-brand-${template.id}-${bid}`}
                >
                  {b?.logo && <span className="text-sm leading-none mr-1.5">{b.logo}</span>}
                  <span className="truncate max-w-[140px]">{b?.name || 'unknown'}</span>
                  {sent ? (
                    <span className="inline-flex items-center gap-0.5 h-6 px-2 ml-1.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white">
                      <Sparkles className="w-2.5 h-2.5" /> sent
                    </span>
                  ) : (
                    isManager && !isGlobal && (
                      <button
                        type="button"
                        onClick={() => onSendBrand(bid)}
                        className="inline-flex items-center gap-0.5 h-6 px-2 ml-1.5 rounded-full text-[10px] font-semibold bg-pink-500 text-white hover:bg-pink-600 shadow-sm"
                        data-testid={`tpl-send-brand-${template.id}-${bid}`}
                      >
                        <Send className="w-2.5 h-2.5" /> draft
                      </button>
                    )
                  )}
                  {canEdit && !sent && (
                    <button
                      type="button"
                      onClick={() => removeBrand(bid)}
                      className="ml-1 mr-1 opacity-40 group-hover:opacity-100 text-inherit"
                      title="remove"
                    >×</button>
                  )}
                </span>
              );
            })}
            {canEdit && (
              <BrandPicker
                brands={brands.filter((b) => !brandIds.includes(b.id))}
                onPick={addBrand}
              />
            )}
            {brandIds.length === 0 && !canEdit && (
              <span className="text-xs italic text-slate-400">no brands</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
const BrandPicker = ({ brands, onPick }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-medium border border-dashed border-pink-300 text-pink-600 hover:bg-pink-50"
          data-testid="tpl-add-brand"
        >
          <Plus className="w-3 h-3" /> add brand
          <ChevronsUpDown className="w-3 h-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="search brand…" className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>no brand found</CommandEmpty>
            <CommandGroup>
              {brands.map((b) => (
                <CommandItem key={b.id} value={`${b.name} ${b.logo || ''}`} onSelect={() => { onPick(b.id); setOpen(false); }}>
                  <Check className="mr-2 h-3.5 w-3.5 opacity-0" />
                  {b.logo && <span className="mr-1.5">{b.logo}</span>}
                  <span className="truncate">{b.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const DuplicatePicker = ({ talents, excludeId, onPick }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold border border-lilac-200 text-lilac-800 hover:bg-lilac-50"
          style={{ background: '#f3ecfb', color: '#5b21b6', borderColor: '#d3c1e1' }}
          data-testid="tpl-duplicate"
          title="duplicate onto a talent"
        >
          <Copy className="w-3 h-3" /> duplicate
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="pick a talent…" className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>no talent</CommandEmpty>
            <CommandGroup>
              {talents.filter((t) => t.user_id !== excludeId).map((t) => (
                <CommandItem key={t.user_id} value={t.name} onSelect={() => { onPick(t.user_id); setOpen(false); }}>
                  <Check className="mr-2 h-3.5 w-3.5 opacity-0" />
                  <span className="truncate">{t.name}</span>
                </CommandItem>
              ))}
              <CommandItem value="__global__" onSelect={() => { onPick(null); setOpen(false); }}>
                <Check className="mr-2 h-3.5 w-3.5 opacity-0" />
                <span className="italic text-slate-500">keep as global draft</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default PitchTemplates;
